import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import showToast from "../../utils/toastNotifications";
import {
  makeExtraPaymentWithPayPal,
  makeExtraPaymentWithWallet,
  setSelectedFormType,
  setSelectedOrder,
  setSelectedThread,
  tipWriterWithPayPal,
  tipWriterWithWallet,
} from "../../store";
import { useThunk } from "../../hooks/useThunk";
import { ClipLoader } from "react-spinners";
import { paymentMethods } from "../../constants/commonConstants";
import Modal from "../Modal";
import LoginInput from "../Form/LoginInput";
import CustomButton from "../Form/ArrowButton";
import { IoClose } from "react-icons/io5";
import { FaCheck } from "react-icons/fa";
import useForm from "../../hooks/useForm";
import { USER_INPUT_FIELD_TYPES } from "../../constants/userInputFieldNames";
import { useSearchParams } from "react-router-dom";
import { canTipWriter } from "../../utils/writerTipEligibility";

const MakeExtraPaymentModal = ({ thread, mode = "payment", hideCloseButton = false }) => {
  const dispatch = useDispatch();
  const isTipMode = mode === "tip" && canTipWriter(thread?.selectedOrder);

  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status");

  useEffect(() => {
    if (mode === "tip" && !isTipMode) {
      showToast("error", "Writer tips are available for 30 days after order completion.");
      dispatch(setSelectedFormType("system"));
    }
  }, [dispatch, isTipMode, mode]);

  useEffect(() => {
    if (status === "success") {
      showToast("success", "Payment completed successfully via PayPal");
    } else if (status === "cancel") {
      showToast("error", "Payment was cancelled");
    }
  }, [status]);

  const [doPayConfirmation, setDoPayConfirmation] = useState({
    bool: false,
    type: "",
  });

  //Thunks
  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
  } = useForm(
    {
      additionalAmount: "",
      paymentOption: "",
    },
    {
      additionalAmount: true,
      paymentOption: true,
    },
    {
      additionalAmount: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      paymentOption: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
    }
  );

  const [
    doMakeExtraPaymentWithWallet,
    isMakeExtraPaymentWithWallet,
    errorMakeExtraPaymentWithWallet,
  ] = useThunk(makeExtraPaymentWithWallet);

  const [
    doCreatePaypalCheckout,
    isCreatePaypalCheckout,
    errorCreatePaypalCheckout,
  ] = useThunk(makeExtraPaymentWithPayPal);

  const [
    doTipWriterWithWallet,
    isTipWriterWithWallet,
    errorTipWriterWithWallet,
  ] = useThunk(tipWriterWithWallet);

  const [
    doCreateTipPaypalCheckout,
    isCreateTipPaypalCheckout,
    errorCreateTipPaypalCheckout,
  ] = useThunk(tipWriterWithPayPal);

  const isProcessing =
    isCreatePaypalCheckout ||
    isMakeExtraPaymentWithWallet ||
    isTipWriterWithWallet ||
    isCreateTipPaypalCheckout;

  const handleSendPayment = () => {
    if (mode === "tip" && !isTipMode) {
      showToast("error", "Writer tips are available for 30 days after order completion.");
      dispatch(setSelectedFormType("system"));
      return;
    }

    // Mark all fields as touched
    setTouched({
      additionalAmount: true,
      paymentOption: true,
    });

    if (!validateForm()) {
      showToast("error", "Please fill all required fields");
    } else {
      setDoPayConfirmation({
        ...doPayConfirmation,
        bool: true,
        type:
          formData.paymentOption === "PayPal"
            ? paymentMethods.PAYPAL
            : paymentMethods.WALLET,
      });
    }
  };

  const handleProceedWithWallet = async () => {
    if (mode === "tip" && !isTipMode) {
      showToast("error", "Writer tips are available for 30 days after order completion.");
      dispatch(setSelectedFormType("system"));
      return;
    }

    const paymentData = {
      ...formData,
      orderId: thread?.selectedOrder?._id,
    };

    const result = isTipMode
      ? await doTipWriterWithWallet(paymentData)
      : await doMakeExtraPaymentWithWallet(paymentData);

    if (result.success) {
      if (isTipMode) {
        dispatch(setSelectedOrder(result?.response?.data || thread.selectedOrder));
        setFormData({
          additionalAmount: "",
          paymentOption: "",
        });
        showToast("success", "Thank you for tipping the writer.");
        dispatch(setSelectedFormType("system"));
        return;
      }

      // Calculate new order price
      const currentPrice = parseFloat(thread?.selectedOrder?.price || 0);
      const additionalAmount = parseFloat(formData.additionalAmount || 0);
      const newPrice = currentPrice + additionalAmount;

      // Update the order in Redux store
      const updatedOrder = {
        ...thread.selectedOrder,
        price: newPrice,
      };

      // Update the thread in Redux store
      const updatedThred = {
        ...thread.selectedThred,
        price: newPrice,
      };

      // Dispatch action to update the thread with new order data
      dispatch(setSelectedOrder(updatedOrder));
      dispatch(setSelectedThread(updatedThred));

      setFormData({
        additionalAmount: "",
        paymentOption: "",
      });

      showToast("success", "Extra payment done successfully");
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("paymentStatus", "success");
      nextSearchParams.set("paymentPurpose", "extra-payment");
      setSearchParams(nextSearchParams, { replace: true });
      dispatch(setSelectedFormType("system"));
    } else {
      showToast("error", result?.error?.message);
    }
  };

  const handleProceedWithPayPal = async () => {
    if (mode === "tip" && !isTipMode) {
      showToast("error", "Writer tips are available for 30 days after order completion.");
      dispatch(setSelectedFormType("system"));
      return;
    }

    setDoPayConfirmation({ ...doPayConfirmation, bool: false, type: "" });

    // Prepare order data to send to the server
    const orderData = {
      items: [
        {
          id: thread?.selectedOrder?._id,
          name: thread?.selectedOrder?._id || "Unknown Item",
          description:
            thread?.selectedOrder?.id + " / $" + formData.additionalAmount ||
            (isTipMode ? "Writer Tip Details" : "Extra Payment Details"),
          quantity: 1,
          unit_price: formData.additionalAmount || "0.00",
        },
      ],
      totalAmount: formData.additionalAmount || "0.00",
      currency: "USD",
    };

    try {
      const result = isTipMode
        ? await doCreateTipPaypalCheckout(orderData)
        : await doCreatePaypalCheckout(orderData);

      if (result?.success) {
        const { approveUrl } = result?.response;
        window.location.href = approveUrl;
      } else {
        showToast(
          "error",
          result?.error?.message || "Failed to create PayPal checkout session"
        );
      }
    } catch (error) {
      showToast("error", error.message || "Error during payment initiation");
    }
  };

  //payment options
  const PAYMENT_OPTIONS = [
    { id: 1, name: "PayPal", label: "PayPal / Card" },
    { id: 2, name: "Wallet", label: "Wallet" },
  ];

  const panelClasses = isTipMode
    ? "relative my-auto w-full max-w-[620px] rounded-[20px] border border-[#f1c77a] bg-gradient-to-br from-[#fffdf7] via-[#fff8e8] to-[#f2fbef] p-3 shadow-[0_24px_60px_rgba(122,82,18,0.12)] sm:rounded-[28px] sm:p-8"
    : "relative my-auto w-full max-w-[620px] rounded-[20px] border border-[#d9e3ff] bg-gradient-to-br from-[#fdfefe] via-[#f8fbff] to-[#edf4ff] p-3 shadow-[0_24px_60px_rgba(0,32,189,0.10)] sm:rounded-[28px] sm:p-8";
  const closeHoverClass = isTipMode ? "hover:bg-[#fff4dc]" : "hover:bg-blue-50";
  const badgeClasses = isTipMode
    ? "inline-flex rounded-full bg-[#fff1cc] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9a5b00]"
    : "inline-flex rounded-full bg-blue-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0020BD]";
  const noteClasses = isTipMode
    ? "mt-5 rounded-[14px] border border-[#f3d18b] bg-[#fff8e8] px-3 py-4 text-sm leading-6 text-[#6b4b14] shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] sm:rounded-[18px] sm:px-4"
    : "mt-5 rounded-[14px] border border-[#d7e4ff] bg-[#f4f8ff] px-3 py-4 text-sm leading-6 text-[#445074] shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] sm:rounded-[18px] sm:px-4";
  const spinnerColor = isTipMode ? "#9a5b00" : "#60a5fa";
  const paymentOptionButtonClasses = (optionName) => {
    const isSelected = formData.paymentOption === optionName;

    return [
      "relative inline-flex h-11 flex-1 items-center justify-center rounded-[8px] border bg-transparent text-sm font-semibold transition-all duration-200",
      "focus:outline-none focus:ring-2 focus:ring-offset-1",
      isTipMode
        ? "border-[#d8ad5b] text-[#6b4b14] hover:border-[#9a5b00] hover:bg-[#fff4dc] hover:text-[#111827] focus:ring-[#f1c77a]"
        : "border-[#9db1f0] text-[#0020BD] hover:border-[#1D4ED8] hover:bg-[#EEF5FF] hover:text-[#111827] focus:ring-[#9db1f0]",
      isSelected
        ? isTipMode
          ? "!border-[#9a5b00] !bg-[#fff1cc] !text-[#111827] shadow-[0_10px_24px_rgba(154,91,0,0.16)] ring-1 ring-[#9a5b00]"
          : "!border-[#1D4ED8] !bg-[#DBEAFE] !text-[#111827] shadow-[0_10px_24px_rgba(29,78,216,0.16)] ring-1 ring-[#1D4ED8]"
        : "",
    ].join(" ");
  };
  const submitButtonHoverClass = isTipMode
    ? "hover:bg-[#B87312]"
    : "hover:bg-[#1D4ED8]";

  return (
    <div className="flex h-full w-full justify-center bg-transparent px-1.5 py-3 sm:px-6 sm:py-8">
      <div className={panelClasses}>
        {!hideCloseButton && (
          <button
            className={`absolute right-4 top-4 rounded-full bg-white/90 p-2 shadow-[0_8px_20px_rgba(15,23,42,0.08)] transition-colors duration-200 ${closeHoverClass}`}
            onClick={() => {
              dispatch(setSelectedFormType("system"));
            }}
          >
            <IoClose className="h-auto w-[24px] text-secondary" />
          </button>
        )}

        <div className="rounded-[16px] border border-[#dfe8ff] bg-white/80 px-3 py-4 shadow-[0_12px_32px_rgba(15,23,42,0.05)] sm:rounded-[22px] sm:px-6 sm:py-5">
          <div className="text-center">
            <span className={badgeClasses}>
              {isTipMode ? "Writer Appreciation" : "Order Payment"}
            </span>
            <h2 className="mt-4 text-xl font-semibold leading-[25px] tracking-normal text-secondary sm:text-[24px]">
              {isTipMode ? "Tip Writer (Optional)" : "Make Extra Payment"}
            </h2>
          </div>

          <div className={noteClasses}>
            {isTipMode
              ? "Send a thank-you tip to the writer for the delivered order. This is optional. The writer will be notified that a tip was made."
              : "This extra payment will be credited to the writer. The writer will be notified that you made an extra payment on this order."}
          </div>

          <div className="mt-6 flex flex-col gap-1">
            {!isTipMode && (
              <LoginInput
                className="w-full"
                id="OriginalOrderAmount"
                name="originalOrderAmount"
                label="Original Order Amount"
                type="text"
                value={
                  thread?.selectedOrder?.price
                    ? `$${parseFloat(thread.selectedOrder.price).toFixed(2)} USD`
                    : "-"
                }
                placeholder={
                  thread?.selectedOrder?.price
                    ? `$${parseFloat(thread.selectedOrder.price).toFixed(2)} USD`
                    : "-"
                }
                outerContainerStyle="w-full"
                isFloatingLabel={false}
                isRequired={false}
                isDisabled={true}
              />
            )}
            <LoginInput
              className="w-full"
              id="additionalAmount"
              name="additionalAmount"
              label={isTipMode ? "Amount To Tip" : "Amount To Add"}
              type="text"
              placeholder="$00.00"
              value={formData.additionalAmount ? `$${formData.additionalAmount}` : ""}
              outerContainerStyle="w-full"
              isFloatingLabel={false}
              isRequired={true}
              onChange={(e) => {
                // Remove $ sign first
                const rawValue = e.target.value.replace(/\$/g, "");
                // Allow only non-negative integers
                if (/^\d*$/.test(rawValue)) {
                  handleInputChange("additionalAmount", rawValue);
                }
              }}
              onBlur={() =>
                handleBlur("additionalAmount", formData.additionalAmount)
              }
              error={touched.additionalAmount && errors.additionalAmount}
            />
            <div className="w-full">
              <label className="mb-2 block text-sm font-medium text-secondary">
                Payment Option <span className="text-red-500">*</span>
              </label>
              <div className="flex w-full gap-2">
                {PAYMENT_OPTIONS.map((option) => {
                  const isSelected = formData.paymentOption === option.name;

                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={paymentOptionButtonClasses(option.name)}
                      onClick={() => {
                        handleInputChange("paymentOption", option.name);
                        handleBlur("paymentOption", option.name);
                      }}
                      aria-pressed={isSelected}
                    >
                      <span>{option.label}</span>
                      {isSelected && (
                        <span className={`absolute right-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-full text-[9px] text-white ${isTipMode ? "bg-[#9a5b00]" : "bg-[#1D4ED8]"}`}>
                          <FaCheck />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {touched.paymentOption && errors.paymentOption && (
                <p className="mt-1 text-xs text-red-500">{errors.paymentOption}</p>
              )}
            </div>
          </div>

          <div className="mt-6 flex w-full flex-col-reverse gap-3 sm:flex-row sm:justify-between">
            <CustomButton
              text="Back"
              iconDirection="left"
              iconPosition="before"
              onClick={() => {
                dispatch(setSelectedFormType("system"));
              }}
              height="44px"
              width="full sm:w-[120px]"
              disabled={isProcessing}
            />
            <CustomButton
              text={
                isProcessing ? (
                  <>
                    <ClipLoader
                      loading={isProcessing}
                      speedMultiplier={0.5}
                      size={22}
                      color={spinnerColor}
                    />
                  </>
                ) : (
                  isTipMode ? "Send Tip" : "Send Payment"
                )
              }
              iconDirection="right"
              iconPosition="after"
              onClick={handleSendPayment}
              height="44px"
              width="full sm:w-[193px]"
              buttonClassName={submitButtonHoverClass}
              disabled={isProcessing}
            />
          </div>
        </div>
      </div>
      {/* confirmation modela */}
      <Modal
        isOpen={doPayConfirmation.bool}
        onClose={() =>
          setDoPayConfirmation({ ...doPayConfirmation, bool: false })
        }
        onConfirm={
          doPayConfirmation.type === paymentMethods.PAYPAL
            ? handleProceedWithPayPal
            : doPayConfirmation.type === paymentMethods.WALLET
            ? handleProceedWithWallet
            : () => {}
        }
        title={isTipMode ? "Please Confirm the Tip" : "Please Confirm the Payment"}
        content={isTipMode ? "Are you sure you want to send this tip to the writer?" : "Are you sure you want to proceed the payment?"}
        cancelText="Cancel"
        confirmText="Process"
        buttonType="success"
      />
    </div>
  );
};

export default MakeExtraPaymentModal;
